import React, { useState } from 'react';
import { Eye, Envelope } from '@phosphor-icons/react';
import { cn, ProfileStatus, updateSession } from '../../lib/utils';
// Cold-start thresholds: hide stats block until profile has real signal.
// Views: 10+ (anything below is noise and psychologically discouraging)
// Offers: 1+ (any offer is a milestone; never hide if real)
const VIEWS_THRESHOLD = 10;
interface ProfileStatusBarProps {
  status: ProfileStatus;
  views: number;
  offers: number;
  onStatusChange?: (next: ProfileStatus) => void;
  onRequestPause?: () => void;
}
export function ProfileStatusBar({
  status,
  views,
  offers,
  onStatusChange,
  onRequestPause
}: ProfileStatusBarProps) {
  const [localStatus, setLocalStatus] = useState<ProfileStatus>(status);
  const showStats = views >= VIEWS_THRESHOLD || offers >= 1;
  const toggleStatus = () => {
    // Intercept active → inactive when caller opts in
    if (localStatus === 'active' && onRequestPause) {
      onRequestPause();
      return;
    }
    const next: ProfileStatus = localStatus === 'active' ? 'inactive' : 'active';
    setLocalStatus(next);
    updateSession({
      profileStatus: next
    });
    onStatusChange?.(next);
  };
  const isActive = localStatus === 'active';
  return <div className="space-y-4" data-id="element-737">
      {/* Inactive banner — top, when profile is paused */}
      {!isActive && <div className="rounded-2xl bg-dark-2 border border-dark-4 p-4 flex items-start gap-3" data-id="element-738">
          <div className="w-2 h-2 rounded-full bg-light-4 mt-2 shrink-0" data-id="element-739" />
          <div className="flex-1 min-w-0" data-id="element-740">
            <p className="text-sm font-medium text-light-1" data-id="element-741">
              Profilul tău e momentan inactiv
            </p>
            <p className="text-xs text-light-3 mt-0.5" data-id="element-742">
              Proprietarii nu îl pot vedea în Marketplace. Reactivează-l când
              ești gata să primești oferte.
            </p>
          </div>
          <button onClick={toggleStatus} className="shrink-0 text-xs font-semibold text-lime-1 hover:text-lime-2 cursor-pointer" data-id="element-743">
            Reactivează
          </button>
        </div>}

      {/* Main strip — toggle + stats only */}
      <div className="rounded-2xl bg-dark-2 border border-dark-3 overflow-hidden" data-id="element-744">
        <div className="px-4 sm:px-5 py-4 flex flex-wrap items-center gap-x-6 gap-y-3" data-id="element-745">
          {/* Status toggle */}
          <button onClick={toggleStatus} className="inline-flex items-center gap-2.5 cursor-pointer group" aria-label={isActive ? 'Marchează inactiv' : 'Marchează activ'} data-id="element-746">
            <span className={cn('relative w-10 h-6 rounded-full transition-colors duration-200', isActive ? 'bg-lime-1' : 'bg-dark-4')} data-id="element-747">
              <span className={cn('absolute top-0.5 left-0.5 w-5 h-5 rounded-full bg-dark-1 transition-transform duration-200', isActive && 'translate-x-4')} data-id="element-748" />
            </span>
            <span className={cn('text-sm font-medium transition-colors', isActive ? 'text-light-1' : 'text-light-3 group-hover:text-light-2')} data-id="element-749">
              {isActive ? 'Activ' : 'Inactiv'}
            </span>
          </button>

          {/* Stats — conditional, only when there's real signal */}
          {showStats && <div className="flex items-center gap-4 text-xs text-light-3" data-id="element-750">
              <span className="inline-flex items-center gap-1.5" data-id="element-751">
                <Eye size={14} weight="regular" data-id="element-752" />
                <span className="text-light-2 font-medium" data-id="element-753">{views}</span>{' '}
                vizualizări
              </span>
              <span className="inline-flex items-center gap-1.5" data-id="element-754">
                <Envelope size={14} weight="regular" data-id="element-755" />
                <span className="text-light-2 font-medium" data-id="element-756">{offers}</span>{' '}
                {offers === 1 ? 'ofertă' : 'oferte'}
              </span>
            </div>}
        </div>
      </div>
    </div>;
}